/*
  Enquire modal — B2B mode.
  Listens for clicks on any [data-enquire-trigger], fills in product context,
  posts the form to /contact (email) and to the configured Slack webhook.
*/
(function () {
  "use strict";

  function init() {
    const modal = document.querySelector("[data-enquire-modal]");
    if (!modal) return;

    const form = modal.querySelector("[data-enquire-form]");
    const statusEl = modal.querySelector("[data-enquire-status]");
    const submitBtn = modal.querySelector("[data-enquire-submit]");
    const slackWebhook = modal.dataset.slackWebhook || "";

    const productTitleEl = modal.querySelector("[data-enquire-product-title]");
    const productSkuEl = modal.querySelector("[data-enquire-product-sku]");
    const productImageEl = modal.querySelector("[data-enquire-product-image]");

    const productTitleInput = modal.querySelector("[data-enquire-product-title-input]");
    const productUrlInput = modal.querySelector("[data-enquire-product-url-input]");
    const productSkuInput = modal.querySelector("[data-enquire-product-sku-input]");
    const productIdInput = modal.querySelector("[data-enquire-product-id-input]");

    function openModal(trigger) {
      const title = trigger.dataset.productTitle || "";
      const url = trigger.dataset.productUrl || "";
      const sku = trigger.dataset.productSku || "";
      const id = trigger.dataset.productId || "";
      const image = trigger.dataset.productImage || "";

      productTitleEl.textContent = title || "—";
      productSkuEl.textContent = sku || "—";
      if (image) {
        productImageEl.src = image;
        productImageEl.style.display = "";
      } else {
        productImageEl.style.display = "none";
      }
      productTitleInput.value = title;
      productUrlInput.value = url;
      productSkuInput.value = sku;
      productIdInput.value = id;

      resetStatus();
      if (typeof modal.showModal === "function") {
        modal.showModal();
      } else {
        // Fallback for browsers without <dialog>
        modal.setAttribute("open", "");
        modal.style.display = "block";
      }

      // Focus first input for accessibility
      const firstInput = form.querySelector('input[name="contact[name]"]');
      if (firstInput) firstInput.focus();
    }

    function closeModal() {
      if (typeof modal.close === "function") modal.close();
      modal.removeAttribute("open");
      modal.style.display = "";
    }

    function resetStatus() {
      if (!statusEl) return;
      statusEl.className = "enquire-modal__status text-sm hidden";
      statusEl.textContent = "";
    }

    function showStatus(kind, message) {
      if (!statusEl) return;
      statusEl.className =
        "enquire-modal__status text-sm py-2 px-3 rounded-sm " +
        (kind === "success"
          ? "bg-green-900/20 text-green-200"
          : "bg-red-900/20 text-red-200");
      statusEl.textContent = message;
    }

    async function postToSlack(payload) {
      if (!slackWebhook) return;
      try {
        // Slack incoming webhook: text/plain avoids CORS preflight.
        await fetch(slackWebhook, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } catch (err) {
        console.warn("[enquire] Slack post failed (non-fatal)", err);
      }
    }

    async function postToShopify(formData) {
      const response = await fetch("/contact", {
        method: "POST",
        body: formData,
        headers: { Accept: "text/html" },
      });
      // Shopify returns 200 on success and redirects to the current URL with ?contact_posted=true
      if (!response.ok && response.status !== 0) {
        throw new Error("Shopify /contact returned " + response.status);
      }
      return response;
    }

    function buildSlackPayload(data) {
      const productTitle = data["contact[Product]"] || "(no product)";
      const productUrl = data["contact[Product URL]"] || "";
      const sku = data["contact[SKU]"] || "—";
      const name = data["contact[name]"] || "—";
      const company = data["contact[Company]"] || "—";
      const email = data["contact[email]"] || "—";
      const phone = data["contact[Phone]"] || "—";
      const qty = data["contact[Quantity]"] || "—";
      const message = data["contact[body]"] || "—";

      return {
        text: "New product enquiry: " + productTitle + " — from " + name + " at " + company,
        blocks: [
          {
            type: "header",
            text: { type: "plain_text", text: "🔔 New Product Enquiry" },
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: "*Product:*\n" + productTitle },
              { type: "mrkdwn", text: "*SKU:*\n" + sku },
              { type: "mrkdwn", text: "*Name:*\n" + name },
              { type: "mrkdwn", text: "*Company:*\n" + company },
              { type: "mrkdwn", text: "*Email:*\n" + email },
              { type: "mrkdwn", text: "*Phone:*\n" + phone },
              { type: "mrkdwn", text: "*Quantity:*\n" + qty },
            ],
          },
          {
            type: "section",
            text: { type: "mrkdwn", text: "*Message:*\n" + message },
          },
          productUrl && {
            type: "section",
            text: { type: "mrkdwn", text: "<" + productUrl + "|View product →>" },
          },
        ].filter(Boolean),
      };
    }

    async function handleSubmit(event) {
      event.preventDefault();
      resetStatus();
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending…";

      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries());

      try {
        await Promise.allSettled([
          postToShopify(formData),
          postToSlack(buildSlackPayload(data)),
        ]);
        showStatus(
          "success",
          "Thanks — we got your enquiry. A team member will be in touch shortly.",
        );
        form.reset();
        setTimeout(closeModal, 2500);
      } catch (err) {
        console.error("[enquire] submit error", err);
        showStatus(
          "error",
          "Something went wrong sending your enquiry. Please email us directly or try again.",
        );
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = "Send Enquiry";
      }
    }

    // Wire up triggers (delegated for dynamically inserted product cards).
    document.addEventListener("click", function (event) {
      const trigger = event.target.closest("[data-enquire-trigger]");
      if (trigger) {
        event.preventDefault();
        openModal(trigger);
        return;
      }
      const closer = event.target.closest("[data-enquire-close]");
      if (closer) {
        event.preventDefault();
        closeModal();
      }
    });

    // Dialog backdrop click closes.
    modal.addEventListener("click", function (event) {
      if (event.target === modal) closeModal();
    });

    form.addEventListener("submit", handleSubmit);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
